// ============================================
// FILE: hooks/useLeads.js
// ============================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import axios from 'axios';
import { socket } from '../lib/Socket';

const API_URL = 'https://demo-api-384317551782.europe-west1.run.app';
// const API_URL = 'http://localhost:3001';

// ── Single lead hook ──────────────────────────────────────────────────────────
export const useLead = (leadId) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!leadId) return;

    const joinLeadRoom = () => {
      socket.emit('join:lead', { leadId });
    };

    if (socket.connected) joinLeadRoom();
    socket.on('connect', joinLeadRoom);

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    };

    const leadEvents = ['lead:updated', 'lead:won', 'lead:lost', 'lead:assigned'];
    leadEvents.forEach((event) => socket.on(event, (data) => {
      if (!data?.leadId || data.leadId === leadId) invalidate();
    }));

    return () => {
      socket.off('connect', joinLeadRoom);
      leadEvents.forEach((event) => socket.off(event));
    };
  }, [leadId, queryClient]);

  return useQuery({
    queryKey: ['lead', leadId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/leads/${leadId}`);
      return data.data;
    },
    enabled:              !!leadId,
    staleTime:            5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry:                2,
  });
};

// ── Lead History hook ─────────────────────────────────────────────────────────
export const useLeadHistory = (leadId) => {
  return useQuery({
    queryKey: ['lead-history', leadId],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/lead/history/${leadId}`);
      return data;
    },
    enabled:              !!leadId,
    staleTime:            2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry:                2,
  });
};

// ── Leads list hook ───────────────────────────────────────────────────────────
export const useLeads = ({ userId, userRole, pagination, columnFilters, sorting }) => {
  const queryClient = useQueryClient();

  const invalidateLeads = () => {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  useEffect(() => {
    if (!userId || !userRole) return;

    const joinRooms = () => {
      socket.emit('join:role', { role: userRole });
      socket.emit('join:user', { userId });
    };

    if (socket.connected) joinRooms();
    socket.on('connect', joinRooms);

    const invalidatingEvents = [
      'lead:created',
      'lead:updated',
      'lead:deleted',
      'lead:won',
      'lead:lost',
      'lead:assigned',
      'bulk:import:complete',
    ];

    invalidatingEvents.forEach((event) => socket.on(event, invalidateLeads));

    return () => {
      socket.off('connect', joinRooms);
      invalidatingEvents.forEach((event) => socket.off(event, invalidateLeads));
    };
  }, [userId, userRole]);

  const query = useQuery({
    queryKey: ['leads', userId, userRole, pagination, columnFilters, sorting],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/leads/${userId}/${userRole}`, {
        params: {
          page:     pagination?.pageIndex ?? 0,
          pageSize: pagination?.pageSize  ?? 10,
          filters:  JSON.stringify(columnFilters ?? []),
          sorting:  JSON.stringify(sorting ?? []),
        },
      });
      return response.data;
    },
    staleTime:                   5 * 60 * 1000,
    refetchInterval:             false,
    refetchOnWindowFocus:        false,
    refetchIntervalInBackground: false,
    refetchOnReconnect:          true,
    keepPreviousData:            true,
    retry:                       2,
    enabled:                     !!userId && !!userRole,
  });

  const createLeadMutation = useMutation({
    mutationFn: async (leadData) => {
      const { data } = await axios.post(`${API_URL}/api/leads`, {
        ...leadData,
        createdBy: userId,
      });
      return data.data;
    },
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, leadData }) => {
      const { data } = await axios.put(`${API_URL}/api/leads/${id}`, leadData);
      return data.data;
    },
    onMutate: async ({ id, leadData }) => {
      await queryClient.cancelQueries({ queryKey: ['leads'] });
      const previousData = queryClient.getQueryData([
        'leads', userId, userRole, pagination, columnFilters, sorting,
      ]);
      queryClient.setQueryData(
        ['leads', userId, userRole, pagination, columnFilters, sorting],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((lead) =>
              lead.id === id ? { ...lead, ...leadData } : lead
            ),
          };
        }
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['leads', userId, userRole, pagination, columnFilters, sorting],
          context.previousData
        );
      }
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (leadId) => {
      const { data } = await axios.delete(`${API_URL}/api/leads/${leadId}`);
      return data.data;
    },
    onMutate: async (leadId) => {
      await queryClient.cancelQueries({ queryKey: ['leads'] });
      const previousData = queryClient.getQueryData([
        'leads', userId, userRole, pagination, columnFilters, sorting,
      ]);
      queryClient.setQueryData(
        ['leads', userId, userRole, pagination, columnFilters, sorting],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((lead) => lead.id !== leadId),
            meta: { ...old.meta, totalCount: (old.meta?.totalCount || 1) - 1 },
          };
        }
      );
      return { previousData };
    },
    onError: (_err, _leadId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ['leads', userId, userRole, pagination, columnFilters, sorting],
          context.previousData
        );
      }
    },
  });

  const bulkImportLeadsMutation = useMutation({
    mutationFn: async (leadsArray) => {
      const { data } = await axios.post(`${API_URL}/api/leads/bulk-import`, {
        leads: leadsArray,
      });
      return data.data;
    },
  });

  return {
    productsApiData:  query.data?.data            || [],
    totalCount:       query.data?.meta?.totalCount || 0,
    isLoading:        query.isLoading,
    isFetching:       query.isFetching,
    error:            query.error,
    fetchProducts:    query.refetch,

    createProduct:    createLeadMutation.mutate,
    updateProduct:    updateLeadMutation.mutate,
    deleteProduct:    deleteLeadMutation.mutate,
    bulkImportLeads:  bulkImportLeadsMutation.mutate,

    isCreating:       createLeadMutation.isPending,
    isUpdating:       updateLeadMutation.isPending,
    isDeleting:       deleteLeadMutation.isPending,
    isBulkImporting:  bulkImportLeadsMutation.isPending,
  };
};

// ── useLeadFiles ──────────────────────────────────────────────────────────────
export const useLeadFiles = (clientId, folder) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId || !folder) return;

    const invalidate = (data) => {
      if (!data?.folder || data.folder === folder) {
        queryClient.invalidateQueries({ queryKey: ['lead-files', clientId, folder] });
      }
    };

    const joinRoom = () => {
      socket.emit('join:lead', { leadId: clientId });
    };

    if (socket.connected) joinRoom();
    socket.on('connect', joinRoom);
    socket.on('files:updated', invalidate);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('files:updated', invalidate);
    };
  }, [clientId, folder, queryClient]);

  return useQuery({
    queryKey:             ['lead-files', clientId, folder],
    queryFn:              async () => {
      const { data } = await axios.get(`${API_URL}/api/files/${clientId}/${folder}`);
      return data.files ?? [];
    },
    enabled:                     !!clientId && !!folder,
    staleTime:                   5 * 60 * 1000,
    refetchOnWindowFocus:        false,
    refetchInterval:             false,
    refetchIntervalInBackground: false,
    refetchOnReconnect:          true,
    retry:                       2,
  });
};

// ── useUploadFiles ────────────────────────────────────────────────────────────
export const useUploadFiles = (clientId) => {
  return useMutation({
    mutationFn: async (historyData) => {
      const { data } = await axios.post(`${API_URL}/api/update-history`, historyData);
      return data;
    },
  });
};